import { useCallback, useEffect, useState } from "react";
import type { ClassObjectives, Objective, ObjectiveProgress } from "@talent/protocol";
import {
  ClassError,
  assignObjective,
  fetchClassObjectives,
  fetchObjectives,
  resetClassAssignments,
  withdrawAssignment,
} from "../class-api.js";
import { COPY, fill, objectiveLabel, strandLabel } from "../copy.js";
import { fa } from "../format.js";
import { ClassBar } from "./class-bar.js";
import { useClasses } from "./class-context.js";
import { ScreenHeader } from "./screen-header.js";

/**
 * The goals a class is working on, and where the next one is handed out.
 *
 * **Rebuilt around the question a teacher arrives with**, which the previous version answered last.
 * That one opened on the catalogue: seven collapsed areas, and behind them seventy-nine objectives.
 * Handing out this week's goal — the thing the screen exists for — took two taps and a scan, and
 * nothing anywhere suggested WHICH goal, even though the catalogue is already written in teaching
 * order. It was reported, fairly, as confusing on a phone.
 *
 * Three sections now, in the order the work happens:
 *
 *   1. **What is already running**, with the class drawn as a bar rather than a pair of counts.
 *   2. **The next step in each area** — one row per strand, one tap each, no browsing.
 *   3. **The whole catalogue**, folded, for going out of order.
 *
 * **Two numbers used to sit side by side meaning different things.** "Given to the class" is
 * something the teacher did; "learnt by three" is something the children did. Both were grey counts
 * on one row, and the file already carried a comment about a teacher reading one as the other. The
 * first is now the section a goal appears IN, and the second is the bar — so they cannot be
 * mistaken for each other because they no longer look alike.
 *
 * `requires` orders what is suggested and locks nothing. Section 2 is a suggestion; section 3 offers
 * every objective in any order, because a teacher who knows something we do not must not be refused.
 */

const CARD = "rounded-lg bg-card shadow-card";
const GIVE = "shrink-0 rounded-pill bg-grape px-md py-1 text-caption text-card disabled:opacity-60";
const QUIET =
  "shrink-0 rounded-pill border border-edge px-md py-1 text-caption text-muted disabled:opacity-60";

/** The busy key for the class-wide reset, which belongs to no single objective. */
const RESET_KEY = "reset-all";

type State =
  | { kind: "loading" }
  | { kind: "failed"; message: string }
  | { kind: "ready"; grid: ClassObjectives; objectives: Objective[] };

type Row = ObjectiveProgress & { studentId: string };

/** How a class stands on one objective. `notStarted` covers untouched rows and absent ones alike. */
interface Standing {
  learnt: Row[];
  practising: Row[];
  notStarted: number;
  /** Live assignments, one per student — the class-wide grant is stored expanded. */
  live: Row[];
}

function standingOf(rows: readonly Row[], classSize: number): Standing {
  const learnt = rows.filter((r) => r.state === "mastered");
  const practising = rows.filter((r) => r.state === "practising");
  return {
    learnt,
    practising,
    notStarted: Math.max(0, classSize - learnt.length - practising.length),
    live: rows.filter((r) => r.assignmentId !== null),
  };
}

export function GoalsScreen({
  onOpenStudent,
  onBack,
  onSignedOut,
}: {
  onOpenStudent: (studentId: string) => void;
  onBack: () => void;
  onSignedOut: () => void;
}) {
  const { current } = useClasses();
  const classId = current?.classId ?? null;
  const [state, setState] = useState<State>({ kind: "loading" });
  const [openStrand, setOpenStrand] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [confirmingReset, setConfirmingReset] = useState(false);
  /**
   * What the last reset did, shown until the screen is used again.
   *
   * Held rather than inferred, because after a reset there is nothing live and nothing live is also
   * what an untouched class shows — a teacher who cleared eleven goals and a teacher who had none
   * would otherwise see the identical screen, and only one of them just pressed a button.
   */
  const [resetMessage, setResetMessage] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!classId) return;
    try {
      // Both at once: the catalogue is static content and the grid is this class's data, and the
      // screen is unusable without either.
      const [catalogue, grid] = await Promise.all([
        fetchObjectives(),
        fetchClassObjectives(classId),
      ]);
      setState({ kind: "ready", grid, objectives: catalogue.objectives });
    } catch (err) {
      if (err instanceof ClassError && err.failure === "unauthenticated") {
        onSignedOut();
        return;
      }
      setState({
        kind: "failed",
        message: err instanceof Error ? err.message : String(err),
      });
    }
  }, [classId, onSignedOut]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * The header is drawn before the data is, on every branch.
   *
   * It was inside the ready branch only, so an empty class — the ordinary state of a new one — got a
   * bare sentence with no title and no way back.
   */
  const framed = (body: React.ReactNode) => (
    <div className="mx-auto min-h-full max-w-[560px] px-lg pt-lg pb-xxl">
      <ScreenHeader
        title={COPY.teacher.goals.title}
        subtitle={COPY.teacher.goals.intro}
        onBack={onBack}
        backLabel={COPY.teacher.goals.back}
      />
      {body}
    </div>
  );

  if (state.kind === "loading") {
    return framed(<p className="p-lg text-center text-muted">{COPY.teacher.loading}</p>);
  }
  if (state.kind === "failed") {
    return framed(
      <p className="p-lg text-center text-muted">
        {fill(COPY.teacher.failed, { message: state.message })}
      </p>,
    );
  }

  const { grid, objectives } = state;
  const classSize = grid.students.length;

  if (classSize === 0) {
    return framed(
      <p className={`${CARD} px-md py-md text-center text-body`}>{COPY.teacher.goals.empty}</p>,
    );
  }

  /** Progress rows keyed by objective, so a lookup is not a scan of the whole class. */
  const byObjective = new Map<string, Row[]>();
  for (const row of grid.progress) {
    const list = byObjective.get(row.objectiveId) ?? [];
    list.push(row);
    byObjective.set(row.objectiveId, list);
  }
  const standing = (objectiveId: string) =>
    standingOf(byObjective.get(objectiveId) ?? [], classSize);

  const act = async (run: Promise<unknown>, key: string) => {
    setBusy(key);
    // Any other action makes the reset notice stale, so it goes.
    if (key !== RESET_KEY) setResetMessage(null);
    // Reloaded either way: the server is the authority on what is assigned, so a failed write and a
    // successful one both resolve by re-reading.
    try {
      await run;
    } finally {
      await load();
      setBusy(null);
    }
  };

  const give = (objectiveId: string) =>
    act(
      assignObjective({ objectiveId, classId: classId as string, students: "all" }),
      objectiveId,
    );

  /**
   * Taking a goal back from the whole class.
   *
   * **Every live assignment, not the first one.** A class-wide grant is stored expanded — one row per
   * child, so it can be withdrawn for one of them — and the previous version withdrew `live[0]` and
   * called it done. A teacher pressing "withdraw" on a goal their whole class had removed it from one
   * child, and the screen came back still showing the goal as live.
   */
  const takeBack = (objectiveId: string, live: readonly Row[]) =>
    act(
      Promise.all(
        live.map((row) => withdrawAssignment(row.assignmentId as string)),
      ),
      objectiveId,
    );

  const strands = groupByStrand(objectives);
  const liveObjectives = objectives.filter((o) => standing(o.id).live.length > 0);

  return framed(
    <>
      {/*
        * What is already running, first.
        *
        * The one thing a teacher wants between two sittings, and it used to be a single grey count
        * above a catalogue. A goal being HERE is what "given" means now — there is no pill saying so,
        * because the section is the statement.
        */}
      <section className="mb-lg">
        <h2 className="mb-sm text-heading">
          {fill(COPY.teacher.goals.liveHeading, { count: fa(liveObjectives.length) })}
        </h2>
        {resetMessage && <p className="mb-sm text-caption text-muted">{resetMessage}</p>}
        {liveObjectives.length === 0 ? (
          <div className={`${CARD} px-md py-md`}>
            <p className="text-body text-muted">{COPY.teacher.goals.noneLive}</p>
            <p className="mt-1 text-caption text-muted">{COPY.teacher.goals.noneLiveHint}</p>
          </div>
        ) : (
          <ul className="flex flex-col gap-sm">
            {liveObjectives.map((objective) => (
              <LiveGoal
                key={objective.id}
                objective={objective}
                standing={standing(objective.id)}
                classSize={classSize}
                grid={grid}
                busy={busy === objective.id}
                onOpenStudent={onOpenStudent}
                onWithdraw={(live) => void takeBack(objective.id, live)}
              />
            ))}
          </ul>
        )}
      </section>

      {/*
        * The next step in each area — the heart of the redesign.
        *
        * One row per strand: the first objective in catalogue order the class has not finished, with
        * the button that hands it over. Seven rows, seven possible taps, no browsing at all.
        *
        * It invents no measure. Catalogue order IS teaching order — that is the promise the whole
        * objective system makes — so "the first one not finished" is a description of where the class
        * has got to rather than a recommendation about anybody.
        */}
      <section className="mb-lg">
        <h2 className="mb-sm text-heading">{COPY.teacher.goals.nextHeading}</h2>
        <div className={`${CARD} px-md`}>
          {strands.map(([strand, inStrand]) => {
            const next = inStrand.find((o) => {
              const s = standing(o.id);
              return s.learnt.length < classSize && s.live.length === 0;
            });
            return (
              <div
                key={strand}
                className="flex items-center gap-sm border-edge py-sm [&+&]:border-t"
              >
                <span className="w-[64px] shrink-0 text-small font-bold">
                  {strandLabel(strand)}
                </span>
                {next ? (
                  <>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-body">{objectiveLabel(next.id)}</span>
                      <span className="block text-caption text-muted">
                        {fill(COPY.teacher.goals.nextRemaining, {
                          count: fa(classSize - standing(next.id).learnt.length),
                        })}
                      </span>
                    </span>
                    <button
                      className={GIVE}
                      disabled={busy === next.id}
                      onClick={() => void give(next.id)}
                    >
                      {busy === next.id ? COPY.enter.working : COPY.teacher.goals.assign}
                    </button>
                  </>
                ) : (
                  <span className="min-w-0 flex-1 text-caption text-muted">
                    {COPY.teacher.goals.nextStrandDone}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/*
        * The whole catalogue, folded.
        *
        * Nothing was taken away: a teacher who wants to hand out something out of order finds it
        * here, in teaching order, with the same controls. It is simply no longer the first thing
        * they meet.
        */}
      <section className="mb-lg">
        <h2 className="mb-sm text-heading">{COPY.teacher.goals.allHeading}</h2>
        <div className="flex flex-col gap-sm">
          {strands.map(([strand, inStrand]) => {
            const done = inStrand.filter((o) => standing(o.id).learnt.length === classSize).length;
            const isOpen = openStrand === strand;
            return (
              <section key={strand} className={CARD}>
                <button
                  className="flex w-full items-center justify-between gap-sm px-md py-sm text-start"
                  onClick={() => setOpenStrand(isOpen ? null : strand)}
                  aria-expanded={isOpen}
                >
                  <span className="text-body">{strandLabel(strand)}</span>
                  <span className="flex items-center gap-sm">
                    <span className="text-caption text-muted tabular-nums">
                      {fill(COPY.teacher.goals.strandSummary, {
                        done: fa(done),
                        total: fa(inStrand.length),
                      })}
                    </span>
                    <Chevron open={isOpen} />
                  </span>
                </button>

                {isOpen && (
                  <ul className="border-t border-edge">
                    {inStrand.map((objective) => {
                      const s = standing(objective.id);
                      return (
                        <li
                          key={objective.id}
                          className="flex items-center gap-sm border-b border-edge px-md py-sm last:border-b-0"
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-body">
                              {objectiveLabel(objective.id)}
                            </span>
                            <span className="block text-caption text-muted">
                              {fill(COPY.teacher.goals.barLearnt, { count: fa(s.learnt.length) })}
                            </span>
                          </span>
                          {s.live.length > 0 ? (
                            <button
                              className={QUIET}
                              disabled={busy === objective.id}
                              onClick={() => void takeBack(objective.id, s.live)}
                            >
                              {COPY.teacher.goals.withdraw}
                            </button>
                          ) : (
                            <button
                              className={GIVE}
                              disabled={busy === objective.id}
                              onClick={() => void give(objective.id)}
                            >
                              {busy === objective.id
                                ? COPY.enter.working
                                : COPY.teacher.goals.assign}
                            </button>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </section>
            );
          })}
        </div>
      </section>

      {/*
        * Clearing the board, at the bottom and quiet.
        *
        * A term's goals in one tap is exactly the kind of action that must not be one tap, and it is
        * also not something a teacher does weekly — so it does not sit beside a number they read
        * every day. The confirmation still says what survives BEFORE offering the button, because the
        * fear here is losing the children's work, and they are not losing it.
        */}
      {liveObjectives.length > 0 && !confirmingReset && (
        <p className="text-center">
          <button
            onClick={() => setConfirmingReset(true)}
            className="px-md py-sm text-caption text-muted"
          >
            {COPY.teacher.goals.reset}
          </button>
        </p>
      )}
      {confirmingReset && (
        <section className={`${CARD} flex flex-col gap-sm px-md py-md`}>
          <h2 className="text-title">{COPY.teacher.goals.resetTitle}</h2>
          <p className="text-small text-muted">{COPY.teacher.goals.resetWarning}</p>
          <button
            disabled={busy === RESET_KEY}
            onClick={() => {
              setConfirmingReset(false);
              void act(
                resetClassAssignments(classId as string).then((withdrawn) => {
                  setResetMessage(
                    withdrawn === 0
                      ? COPY.teacher.goals.resetNothing
                      : fill(COPY.teacher.goals.resetDone, { count: fa(withdrawn) }),
                  );
                }),
                RESET_KEY,
              );
            }}
            className="rounded-pill bg-wrong px-md py-sm text-small text-card disabled:opacity-60"
          >
            {busy === RESET_KEY ? COPY.enter.working : COPY.teacher.goals.resetConfirm}
          </button>
          <button onClick={() => setConfirmingReset(false)} className="py-1 text-small text-muted">
            {COPY.teacher.goals.resetCancel}
          </button>
        </section>
      )}
    </>,
  );
}

/**
 * One goal the class is on, with the class drawn rather than counted.
 *
 * The bar has three parts because there are three states and no fourth, and every child is in
 * exactly one of them. A percentage would be a number with no referent, and it would also hide the
 * distinction a teacher acts on: a child who has not started and a child who is stuck are different
 * problems with different answers.
 *
 * The names are shown for all three states, not only the finished ones. "Who has not started yet"
 * is the next question every teacher asks and the old screen had nowhere to put it.
 */
function LiveGoal({
  objective,
  standing,
  classSize,
  grid,
  busy,
  onOpenStudent,
  onWithdraw,
}: {
  objective: Objective;
  standing: Standing;
  classSize: number;
  grid: ClassObjectives;
  busy: boolean;
  onOpenStudent: (studentId: string) => void;
  onWithdraw: (live: readonly Row[]) => void;
}) {
  const started = new Set([
    ...standing.learnt.map((r) => r.studentId),
    ...standing.practising.map((r) => r.studentId),
  ]);
  const notStarted = grid.students.filter((s) => !started.has(s.studentId));

  return (
    <li className={`${CARD} flex flex-col gap-sm px-md py-md`}>
      <span className="flex items-baseline gap-sm">
        <span className="min-w-0 flex-1 text-body font-bold">{objectiveLabel(objective.id)}</span>
        <span className="shrink-0 rounded-pill bg-edge px-sm py-1 text-caption text-muted">
          {strandLabel(objective.strand)}
        </span>
      </span>

      <ClassBar
        learnt={standing.learnt.length}
        practising={standing.practising.length}
        total={classSize}
      />

      <span className="flex flex-wrap gap-x-md gap-y-1 text-caption text-muted">
        <span className="flex items-center gap-1">
          <i className="inline-block size-[10px] rounded-xs bg-correct" />
          {fill(COPY.teacher.goals.barLearnt, { count: fa(standing.learnt.length) })}
        </span>
        <span className="flex items-center gap-1">
          <i className="inline-block size-[10px] rounded-xs bg-sky" />
          {fill(COPY.teacher.goals.barPractising, { count: fa(standing.practising.length) })}
        </span>
        <span className="flex items-center gap-1">
          <i className="inline-block size-[10px] rounded-xs bg-edge" />
          {fill(COPY.teacher.goals.barNotStarted, { count: fa(standing.notStarted) })}
        </span>
      </span>

      <span className="flex flex-wrap gap-1">
        {standing.learnt.map((row) => (
          <Name key={row.studentId} grid={grid} id={row.studentId} tone="bg-correct/28" onOpen={onOpenStudent} />
        ))}
        {standing.practising.map((row) => (
          <Name key={row.studentId} grid={grid} id={row.studentId} tone="bg-sky/22" onOpen={onOpenStudent} />
        ))}
        {notStarted.map((student) => (
          <Name
            key={student.studentId}
            grid={grid}
            id={student.studentId}
            tone="bg-edge"
            onOpen={onOpenStudent}
          />
        ))}
      </span>

      <span className="flex justify-end">
        <button className={QUIET} disabled={busy} onClick={() => onWithdraw(standing.live)}>
          {busy ? COPY.enter.working : COPY.teacher.goals.withdraw}
        </button>
      </span>
    </li>
  );
}

function Name({
  grid,
  id,
  tone,
  onOpen,
}: {
  grid: ClassObjectives;
  id: string;
  tone: string;
  onOpen: (studentId: string) => void;
}) {
  return (
    <button
      onClick={() => onOpen(id)}
      className={`rounded-pill px-sm py-1 text-caption text-ink ${tone}`}
    >
      {nameOf(grid, id)}
    </button>
  );
}

function nameOf(grid: ClassObjectives, studentId: string): string {
  const student = grid.students.find((s) => s.studentId === studentId);
  return student?.firstName ?? "";
}

/** Catalogue order within a strand, strand order as the catalogue lists them. */
function groupByStrand(objectives: readonly Objective[]): [string, Objective[]][] {
  const groups = new Map<string, Objective[]>();
  for (const objective of objectives) {
    const list = groups.get(objective.strand) ?? [];
    list.push(objective);
    groups.set(objective.strand, list);
  }
  return [...groups.entries()];
}

/**
 * An SVG, not a character.
 *
 * `›` is in the Unicode bidi-mirroring set, so inside an RTL element the browser flips it and the
 * glyph points the opposite way from the one written. An SVG has no bidi behaviour.
 */
function Chevron({ open }: { open: boolean }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      aria-hidden
      className={`shrink-0 text-muted transition-transform ${open ? "rotate-90" : ""}`}
    >
      <path
        d="M15 5l-7 7 7 7"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
